import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../lib/api";

interface AuthValue {
  authenticated: boolean;
  loading: boolean;
  loginName: string;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [loginName, setLoginName] = useState("");

  const loadSession = useCallback(async () => {
    try {
      const session = await api.authSession();
      setLoginName(session.authenticated ? session.user?.login || "" : "");
    } catch {
      setLoginName("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadSession(); }, [loadSession]);
  useEffect(() => {
    const unauthorized = () => setLoginName("");
    globalThis.addEventListener("learning-portal:unauthorized", unauthorized);
    return () => globalThis.removeEventListener("learning-portal:unauthorized", unauthorized);
  }, []);

  const login = useCallback(async (loginValue: string, password: string) => {
    const session = await api.login(loginValue, password);
    setLoginName(session.user?.login || loginValue.trim().toLowerCase());
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setLoginName("");
  }, []);

  const value = useMemo<AuthValue>(() => ({
    authenticated: Boolean(loginName),
    loading,
    loginName,
    login,
    logout,
  }), [loading, loginName, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.loading) return <div className="loading-state">正在检查登录状态</div>;
  if (!auth.authenticated) return <LoginPage />;
  return children;
}

function LoginPage() {
  const { login } = useAuth();
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(loginName, password);
      setPassword("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <form className="login-card" onSubmit={(event) => void submit(event)}>
        <span className="login-mark" aria-hidden="true">LP</span>
        <h1>登录学习门户</h1>
        <p>请输入门户所有者账号。登录成功后才能访问学习数据、资料和练习。</p>
        <label><span>账号</span><input aria-label="账号" autoComplete="username" required value={loginName} onChange={(event) => setLoginName(event.target.value)} /></label>
        <label><span>密码</span><input aria-label="密码" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="command-button" type="submit" disabled={busy}>{busy ? "正在登录" : "登录"}</button>
      </form>
    </main>
  );
}
